import React, { useMemo } from 'react';

interface RatingComparisonItem {
    // Name/label fields
    name?: string;
    cycleName?: string;
    competencyTitle?: string;
    reviewName?: string;
    title?: string;
    label?: string;
    // Rating fields - can be single value or multiple
    value?: number | null;
    rating?: number | null;
    score?: number | null;
    employeeRating?: number | null;
    managerRating?: number | null;
    selfRating?: number | null;
    [key: string]: unknown;
}

interface SeriesConfig {
    key: string;
    label: string;
    color: string;
}

interface RatingComparisonListProps {
    data: Array<Record<string, unknown>>;
    options?: {
        show_legend?: boolean;
        max_items?: number;
        max_rating?: number;
    };
    series?: SeriesConfig[];
}

// Helper to get display name from normalized item
const getItemName = (item: RatingComparisonItem): string => {
    return item.name
        || item.cycleName
        || item.competencyTitle
        || item.reviewName
        || item.title
        || item.label
        || 'Unknown';
};

// Helper to safely get number value (handles null/undefined)
const safeNumber = (val: unknown): number | null => {
    if (val === null || val === undefined || val === '') return null;
    const num = Number(val);
    return isNaN(num) ? null : num;
};

// Helper to get single rating value from item
const getSingleRating = (item: RatingComparisonItem): number | null => {
    return safeNumber(item.value ?? item.rating ?? item.score);
};

// Default series configuration for dual ratings
const defaultSeries: SeriesConfig[] = [
    { key: 'employeeRating', label: 'Self Rating', color: '#4CAF50' },
    { key: 'managerRating', label: 'Manager Rating', color: '#2196F3' },
];

// Get color based on rating value (0-5 scale)
const getRatingColor = (value: number): string => {
    if (value >= 4) return '#22c55e'; // green-500
    if (value >= 3) return '#eab308'; // yellow-500
    return '#ef4444'; // red-500
};

export const RatingComparisonList: React.FC<RatingComparisonListProps> = ({
    data,
    options = {},
    series,
}) => {
    const {
        show_legend = true,
        max_items = 10,
        max_rating = 5,
    } = options;

    const items = useMemo(() => {
        if (!data) return [];
        const dataArray = Array.isArray(data) ? data : (data as any).data;
        if (!Array.isArray(dataArray)) return [];
        return dataArray.slice(0, max_items) as RatingComparisonItem[];
    }, [data, max_items]);

    // Detect if data has multi-series (employeeRating/managerRating) or single value
    const isMultiSeries = useMemo(() => {
        if (!items.length) return false;
        const firstItem = items[0];
        const keys = (series || defaultSeries).map(s => s.key);
        return keys.some(key => firstItem[key as keyof RatingComparisonItem] !== undefined);
    }, [items, series]);

    const seriesKeys = useMemo(() => {
        return series || defaultSeries;
    }, [series]);

    if (items.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No data available
            </div>
        );
    }

    // Single value mode (like avg rating per cycle)
    if (!isMultiSeries) {
        return (
            <div className="flex flex-col h-full overflow-auto p-3">
                <div className="flex-1 overflow-auto space-y-2">
                    {items.map((item, index) => {
                        const name = getItemName(item);
                        const rating = getSingleRating(item);
                        const hasRating = rating !== null && rating > 0;

                        return (
                            <div
                                key={name + index}
                                className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <span className="text-sm font-medium text-gray-900 truncate flex-1 mr-3" title={name}>
                                    {name}
                                </span>

                                {hasRating ? (
                                    <div
                                        className="flex items-center justify-center w-10 h-10 rounded-full font-bold text-white text-sm flex-shrink-0"
                                        style={{ backgroundColor: getRatingColor(rating) }}
                                    >
                                        {rating.toFixed(1)}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-400 text-xs flex-shrink-0">
                                        —
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Multi-series mode (employee_rating vs manager_rating)
    return (
        <div className="flex flex-col h-full overflow-auto p-3">
            {/* Legend */}
            {show_legend && (
                <div className="flex items-center gap-4 mb-3 pb-2 border-b border-gray-100">
                    {seriesKeys.map((s) => (
                        <div key={s.key} className="flex items-center gap-2">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: s.color }}
                            />
                            <span className="text-xs text-gray-600">{s.label}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Items */}
            <div className="flex-1 overflow-auto space-y-3">
                {items.map((item, index) => {
                    const name = getItemName(item);

                    // Get ratings for each series
                    const ratings = seriesKeys.map(s => {
                        const val = item[s.key as keyof RatingComparisonItem];
                        return {
                            ...s,
                            value: safeNumber(val),
                        };
                    });

                    const hasAnyRating = ratings.some(r => r.value !== null);

                    return (
                        <div
                            key={name + index}
                            className="bg-white border border-gray-100 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                        >
                            {/* Item Name */}
                            <div className="text-sm font-medium text-gray-900 mb-2 truncate" title={name}>
                                {name}
                            </div>

                            {/* Rating Bars */}
                            {hasAnyRating ? (
                                <div className="space-y-2">
                                    {ratings.map((rating) => (
                                        <div key={rating.key} className="flex items-center gap-2">
                                            {/* Rating Bar */}
                                            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                                                {rating.value !== null && (
                                                    <div
                                                        className="h-full rounded-full transition-all duration-300"
                                                        style={{
                                                            width: `${Math.min((rating.value / max_rating) * 100, 100)}%`,
                                                            backgroundColor: rating.color,
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            {/* Rating Value */}
                                            <span
                                                className="text-xs font-semibold w-8 text-right"
                                                style={{ color: rating.value !== null ? rating.color : '#9ca3af' }}
                                            >
                                                {rating.value !== null ? rating.value.toFixed(1) : '—'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-xs text-gray-400 italic">No ratings available</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RatingComparisonList;
